<template>
    <Splide :has-track="false" class="splide__gallery splide__gallery--nearby-only h-full -mx-4 lg:mx-0" :options="options" @splide:mounted="loadNearbySoon" @splide:dragging="loadNearby" @splide:move="loadNearby">
        <SplideTrack class="h-full">
            <SplideSlide
                v-for="(item, index) in images" :key="item.id"
                class="overflow-hidden"
            >
                <!-- Whole photos, cropped once here around their focus point. The box is 290px tall on phones
                     and most of the screen's height on tablets, so the photo shows wider than the screen. Keep
                     sizes and srcset in step with hero-image.php, and ahead of src: Safari starts downloading
                     as each one is set -->
                <img class="lg:h-[790px] w-full h-[290px] sm:h-full object-cover sm:aspect-auto select-none"
                    v-if="item.src" :loading="index === 0 ? 'eager' : 'lazy'"
                    sizes="(min-width: 1480px) 1920px, (min-width: 1000px) 1480px, (min-width: 640px) 1100px, 115vw"
                    :srcset="item.srcset" :src="`${item.src}?tr=w-700`"
                    :style="{ objectPosition: item.focus }" :alt="`${item.alt} image`">
            </SplideSlide>
        </SplideTrack>

        <div class="splide__arrows flex justify-between absolute top-1/2 left-0 w-full -mt-8 px-8">
            <button class="splide__arrow--prev w-11 h-11 flex justify-center items-center rounded-full transition-all duration-200"
                    :class="pageStyle ? `bg-${pageStyle}-primary hover:bg-${pageStyle}-primary-tone` : 'bg-primary hover:bg-primary-tone'">
                <svg class="relative -left-px fill-dark" width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.22748 8.80219L2.37798 15.6517C1.94989 16.1155 1.20072 16.1155 0.772631 15.6517C0.308863 15.2236 0.308863 14.4744 0.772631 14.0463L6.80162 7.98167L0.772631 1.95269C0.308863 1.5246 0.308863 0.775431 0.772631 0.347338C1.20072 -0.11643 1.94989 -0.11643 2.37798 0.347338L9.22748 7.19684C9.69125 7.62493 9.69125 8.37409 9.22748 8.80219Z" />
                </svg>
            </button>

            <button class="splide__arrow--next w-11 h-11 flex justify-center items-center rounded-full transition-all duration-200"
                    :class="pageStyle ? `bg-${pageStyle}-primary hover:bg-${pageStyle}-primary-tone` : 'bg-primary hover:bg-primary-tone'">
                <svg class="relative -right-px fill-dark" width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.22748 8.80219L2.37798 15.6517C1.94989 16.1155 1.20072 16.1155 0.772631 15.6517C0.308863 15.2236 0.308863 14.4744 0.772631 14.0463L6.80162 7.98167L0.772631 1.95269C0.308863 1.5246 0.308863 0.775431 0.772631 0.347338C1.20072 -0.11643 1.94989 -0.11643 2.37798 0.347338L9.22748 7.19684C9.69125 7.62493 9.69125 8.37409 9.22748 8.80219Z" />
                </svg>
            </button>
        </div>
    </Splide>
</template>

<script>
import {defineComponent} from 'vue'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide';
import ResponsiveMixin from "./../mixins/responsive"

const PHOTO_WIDTHS = [700, 1000, 1400, 1480, 1920, 2200, 2960, 3840]
const NEARBY_DELAY = 2000

export default defineComponent({
    components: {
        Splide,
        SplideSlide,
        SplideTrack,
    },
    mixins: [ResponsiveMixin],
    props: {
        pageStyle: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            options: {
                type: 'loop',
                perPage: 1,
                arrows: true,
                pagination: false,
                breakpoints: {
                    768: {
                        arrows: false,
                        pagination: true,
                    },
                },
            },
        }
    },
    computed: {
        images() {
            const rawImages = window.capes?.experience_gallery
            const rawHero = window.capes?.hero_image

            const gallery = Array.isArray(rawImages) ? rawImages.slice() : []

            const heroId = rawHero?.ID ?? rawHero?.id
            const heroUrl = rawHero?.url
            const hasHero = !!(rawHero && (heroId || heroUrl || rawHero?.sizes))

            const alreadyHasHero = hasHero
                ? gallery.some((img) => {
                    const imgId = img?.ID ?? img?.id
                    const imgUrl = img?.url
                    return (heroId && imgId && imgId === heroId) || (heroUrl && imgUrl && imgUrl === heroUrl)
                })
                : false

            const combined = hasHero && !alreadyHasHero ? [rawHero, ...gallery] : gallery

            return combined.filter(Boolean).map((image) => {
                return {
                    id: image.ID ?? image.id ?? image.url,
                    // The full-size file: the image Worker resizes it to each ?tr= size, and the
                    // 2048px copy was too small for the 2x desktop sizes
                    src: image.url,
                    srcset: PHOTO_WIDTHS.map((width) => `${image.url}?tr=w-${width} ${width}w`).join(', '),
                    thumb: `${image.sizes?.medium_large ?? image.url}`,
                    alt: image.alt || (image.title + ' image'),
                    focus: image.focus || '50% 50%',
                }
            })
        },
    },
    methods: {
        // Only the first slide loads with the page: splide__gallery--nearby-only hides the others'
        // photos, because a lazy image still loads if it's near the screen. The next and previous
        // photos follow once the page has settled, and move along with the carousel, so a swipe
        // finds its photo ready without everyone downloading the lot. Splide's loop clones aren't
        // Vue's, so this goes via the DOM. dragging, not drag, as drag also fires when a finger on
        // the photo scrolls the page
        loadNearby(splide) {
            const { index, length } = splide
            const nearby = [index - 1, index, index + 1].map((i) => (i + length) % length)

            splide.Components.Slides.forEach((slide) => {
                if (nearby.includes(slide.isClone ? slide.slideIndex : slide.index)) {
                    slide.slide.classList.add('is-nearby')
                    slide.slide.querySelectorAll('img[loading="lazy"]').forEach((img) => {
                        img.loading = 'eager'
                    })
                }
            })
        },
        loadNearbySoon(splide) {
            const load = () => setTimeout(() => this.loadNearby(splide), NEARBY_DELAY)

            if (document.readyState === 'complete') {
                load()
            } else {
                window.addEventListener('load', load, { once: true })
            }
        },
    },
    created() {
        // console.log(this.images)
    },
})
</script>